import type { ReactNode } from 'react'
import { MoreVertical } from 'lucide-react'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'

export interface RowAction {
  /** Stable React key — the action's id, not its (translated) label. */
  key: string
  label: string
  icon: ReactNode
  onSelect: () => void
  disabled?: boolean
}

interface Props {
  actions: RowAction[]
  /** Accessible name for the trigger; the frame leaves it unlabelled. */
  label: string
}

/**
 * The row-actions kebab, shared by Matched and Pending.
 *
 * Figma 11. Edit (1232:284987) draws the SAME control on both tabs and differs
 * only in the items, so the two tables take it from here rather than each
 * growing their own copy — that is how the two drifted apart last time.
 *
 * Measurements, from the frame:
 *
 * * The trigger is drawn on the HOVERED row ONLY — every other row's actions
 *   cell (e.g. 1232:285250) is empty. Hence `opacity-0` lifted by the row's
 *   `group/row` hover. Opacity rather than `hidden`, because a keyboard user
 *   never hovers: `focus-visible` reveals it too, and `data-[state=open]`
 *   keeps it up while its own menu is open (Radix stamps that on the trigger).
 * * Trigger icon: EllipsisVertical at 20px (`size-5`), centred in a 46px cell.
 * * Menu (1232:285266 / 1232:286147): 130px wide, `p-1`, `rounded-lg`, items
 *   32px tall — `p-1.5` around a 20px line — with a 16px icon at `gap-1.5`.
 *   `align="end"` is what puts the menu's right edge on the trigger's, which
 *   is where the frame draws it (menu x 929..1059 against a trigger ending
 *   at 1059).
 */
const RowActionsMenu = ({ actions, label }: Props) => {
  if (actions.length === 0) return null

  return (
    <IntuiDropdownMenu>
      <IntuiDropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="mx-auto flex size-5 items-center justify-center rounded-md text-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/row:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreVertical className="size-5" aria-hidden />
        </button>
      </IntuiDropdownMenuTrigger>
      <IntuiDropdownMenuContent align="end" className="min-w-[130px]">
        {actions.map((action) => (
          <IntuiDropdownMenuItem
            key={action.key}
            icon={action.icon}
            disabled={action.disabled}
            onSelect={action.onSelect}
            // The Intueri item is py-1 (28px); the frame's is 32px, i.e. the
            // same 6px it already uses horizontally, applied on both axes.
            className="py-1.5"
          >
            {action.label}
          </IntuiDropdownMenuItem>
        ))}
      </IntuiDropdownMenuContent>
    </IntuiDropdownMenu>
  )
}

export default RowActionsMenu
